import React, { FC, useContext, useMemo, useState } from 'react';

import Image from 'next/image';
import { useTranslation } from 'next-i18next/pages';

import { Button, Tooltip } from 'antd';
import cn from 'classnames';

import { createPaymentRequest } from '@/api/tariff';
import IncludedIcon from '@/assets/included-icon.svg';
import PromptIcon from '@/assets/prompt-icon.svg';
import UnincludedIcon from '@/assets/unincluded-icon.svg';
import { getLocalizedTariffName } from '@/components/TariffModal/utils';
import { ApplicationPaymentType } from '@/constants/application-payments';
import { FeedbackContext } from '@/context/feeadback.context';
import { TranslationNamespace } from '@/i18n';
import { Tariff } from '@/types/tariff';

import { InvoiceModal } from '../InvoiceModal/InvoiceModal';
import { InvoiceFieldType } from '../InvoiceModalContent/InvoiceModalContent';
import { TariffsTableProps } from '../types';
import { getTariffsInitialState } from '../utils';

import styles from './TariffsTableMobile.module.scss';

export const TariffsTableMobile: FC<TariffsTableProps> = ({
	tariffsData,
	onTariffChoice,
	showButtons,
	tariffChoiceDisable,
	currentTariff,
	isActiveTariff,
	userIsAuth,
}) => {
	const [isShowMore, setIsShowMore] = useState<boolean>(true);
	const [slicedItems, setSlicedItems] = useState(getTariffsInitialState(tariffsData));
	const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(false);
	const { notify } = useContext(FeedbackContext);

	const t = useTranslation(TranslationNamespace.Marketing).t;

	const showModal = () => {
		setIsOpenModal(true);
	};

	const hideModal = () => {
		setIsOpenModal(false);
	};

	const handleClickVipTariff = () => {
		showModal();
	};

	const handleSendInvoice = async (formData: InvoiceFieldType) => {
		const { name, phone, email, comment } = formData;

		try {
			setIsLoading(true);

			await createPaymentRequest({
				type: ApplicationPaymentType.Tariff,
				infoForRequest: {
					name,
					phone,
					email,
					comment,
				},
				tariff: tariffByRequest.id,
			});

			notify.success({
				title: t('tariffs-table.your-application-has-been-successfully-sent'),
			});
			hideModal();
		} catch (error) {
			notify.error({
				title: error?.message ?? t('tariffs-table.an-error-occurred-while-sending-the-application'),
				description: t('tariffs-table.please-try-again'),
			});
		} finally {
			setIsLoading(false);
		}
	};

	const tariffByRequest = useMemo(() => {
		return tariffsData?.tariffs.find((tariff) => tariff.applyOnRequest);
	}, [tariffsData]);

	const renderButton = (tariff: Tariff.TariffWithSettings) => {
		const tariffButtonText =
			tariff.id === Tariff.TariffId.PlatinumDiamond
				? 'tariffs-table.request'
				: 'tariffs-table.choose';

		if (!showButtons) return null;

		if (!userIsAuth) return getButton(tariff, t(tariffButtonText));

		if ((currentTariff && !isActiveTariff) || !currentTariff) {
			return getButton(tariff, t(tariffButtonText));
		}

		if (currentTariff === tariff.id)
			// true for old perYear flag
			return <p className={styles.currentTariff}>{t('tariffs-table.current-tariff')}</p>;

		return getButton(tariff, t('tariffs-table.change'));
	};

	const getButton = (tariff: Tariff.TariffWithSettings, text: string) => {
		if (tariff.id === Tariff.TariffId.Silver) {
			return (
				<Button
					className={styles.choiceButton}
					type="primary"
					onClick={() => onTariffChoice({ tariffInfo: tariff, per_three_month: true })}
					disabled={tariffChoiceDisable}
				>
					{text}
				</Button>
			);
		}
		if (tariff.id === Tariff.TariffId.Gold) {
			return (
				<Button
					className={styles.choiceButton}
					type="primary"
					onClick={() => onTariffChoice({ tariffInfo: tariff, perYear: true })}
					disabled={tariffChoiceDisable}
				>
					{text}
				</Button>
			);
		}
		if (tariff.id === Tariff.TariffId.PlatinumDiamond) {
			return (
				<Button
					className={styles.choiceButton}
					type="primary"
					onClick={handleClickVipTariff}
					disabled={tariffChoiceDisable}
				>
					{text}
				</Button>
			);
		}
	};

	const renderSettingCell = (setting: Tariff.Setting) => {
		if (typeof setting?.value === 'string') {
			const isWithoutCommission = ['без комиссии', 'without commission'].includes(
				setting.value.toLowerCase(),
			);

			return isWithoutCommission ? (
				<div className={styles.withoutCommission}>{t('tariffs-table.without-commission')}</div>
			) : (
				<div>{setting?.value}</div>
			);
		}

		return setting?.value ? (
			<Image className={styles.icon} width={24} height={24} alt="" src={IncludedIcon} />
		) : (
			<Image className={styles.icon} width={24} height={24} alt="" src={UnincludedIcon} />
		);
	};

	const handleClickShowMore = () => {
		if (isShowMore) {
			setSlicedItems(tariffsData?.tariffsInfo);
		} else {
			setSlicedItems(getTariffsInitialState(tariffsData));
		}

		setIsShowMore(!isShowMore);
	};

	return (
		<div className={styles.container}>
			<h2 className={styles.tariffHeader}>{t('tariffs-table.tariffs')}</h2>
			<div className={styles.tariffDate}>{t('tariffs-table.effective-from-may-8-2026')}</div>

			<div className={styles.tariff}>
				{tariffsData?.tariffs.map((tariff) => {
					return (
						<div key={tariff.id}>
							{tariff.bestChoice && (
								<div className={styles.bestChoiceLabel}>{t('tariffs-table.choose-suppliers')}</div>
							)}
							<div className={styles.tariffSection}>
								<div className={styles.tariffInfo}>
									<div className={styles.tariffName}>
										{getLocalizedTariffName(tariff.id, tariff.name, t)}
									</div>
									<div className={styles.tariffPrompt}>{tariff.prompt}</div>
									<div className={styles.tariffParameters}>
										{slicedItems.map((tariffSetting, index) => {
											const tariffSettingInfo = tariff.settings.find(
												(item) => item.infoId === tariffSetting.id,
											);
											const isFirstItem = index === 0;

											return (
												<div className={styles.tariffSettingPoint} key={tariffSettingInfo?.infoId}>
													<div className={styles.tariffSettingTitle}>
														<div className={styles.tariffSettingLabel}>{tariffSetting.name}</div>
														{tariffSetting.prompt && (
															<Tooltip title={tariffSetting.prompt}>
																<Image className={styles.hint} src={PromptIcon} alt="" />
															</Tooltip>
														)}
													</div>
													<div
														className={cn(styles.tariffSettingValue, {
															[styles.boldValue]: isFirstItem,
														})}
													>
														{renderSettingCell(tariffSettingInfo)}
													</div>
													{tariffSetting.description && (
														<div className={styles.tariffSettingDescription}>
															{tariffSetting.description}
														</div>
													)}
												</div>
											);
										})}
									</div>
									<Button className={styles.showMoreButton} onClick={handleClickShowMore}>
										{t(
											isShowMore
												? 'tariffs-table.show-all-benefits'
												: 'tariffs-table.hide-benefits',
										)}
									</Button>
									{renderButton(tariff)}
								</div>
							</div>
						</div>
					);
				})}
			</div>
			{isOpenModal && (
				<InvoiceModal
					isOpenModal={isOpenModal}
					description={t('tariffs-table.vip-description')}
					isLoading={isLoading}
					title={t('tariffs-table.vip-title')}
					onHideModal={hideModal}
					onSendInvoice={handleSendInvoice}
				/>
			)}
		</div>
	);
};
